import { PageRenderer, RenderedPage, fetchFile, fitFactor } from './page-renderer';

/** Width slides are laid out at before zoom; the library scales the deck to it. */
const SLIDE_WIDTH_PX: number = 960;

/**
 * PowerPoint (`.pptx`) via pptx-preview — every slide stacked as an HTML element
 * (`.pptx-preview-slide-wrapper`).
 *
 * A preview, not PowerPoint: animations are dropped, and SmartArt or some charts may differ. The
 * legacy binary `.ppt` is not supported by the library; the viewer never routes one here.
 */
export class PptxRenderer implements PageRenderer {

    readonly paged: boolean = false;
    readonly hasText: boolean = true;

    private host: HTMLElement | null = null;
    private body: HTMLElement | null = null;
    private previewer: { destroy(): void } | null = null;

    async render(src: string, host: HTMLElement): Promise<RenderedPage[]> {
        this.host = host;
        const { init } = await import('pptx-preview');
        const data: ArrayBuffer = await fetchFile(src);

        const body: HTMLDivElement = document.createElement('div');
        body.className = 'pv-pptx';
        host.appendChild(body);
        this.body = body;

        // `list` stacks every slide; leaving out `height` keeps the library from adding its own
        // scroll box inside ours.
        const previewer = init(body, { width: SLIDE_WIDTH_PX, mode: 'list' });
        this.previewer = previewer;
        await previewer.preview(data);

        const slides: HTMLElement[] = Array.from(body.querySelectorAll<HTMLElement>('.pptx-preview-slide-wrapper'));
        slides.forEach((slide) => slide.classList.add('pv-page', 'is-slide'));

        await this.zoom(100);
        return slides.map((el, index) => ({ el, label: `Slide ${index + 1}` }));
    }

    async zoom(zoom: number): Promise<void> {
        if (!this.host || !this.body) {
            return;
        }
        this.body.style.setProperty('zoom', String(fitFactor(this.host, SLIDE_WIDTH_PX) * (zoom / 100)));
    }

    destroy(): void {
        this.previewer?.destroy();
        this.previewer = null;
        this.body = null;
        this.host = null;
    }
}
